/**
 * The facts contract, version 1, as types. Build round 18 (2026-10-07), decision 0503.
 *
 * `docs/facts-schema.md` is the contract in words and `docs/facts-schema.v1.json` the meta-schema a
 * site's build validates its `content/facts.schema.json` against. The database is the authority on
 * both a schema and a set of values (`private.site_facts_schema_problems` and
 * `private.site_facts_value_problems`, migration `20261007500001`); `./validate.ts` repeats the value
 * rules in the browser for speed only, and a shared corpus (`tests/fixtures/facts/corpus.json`) holds
 * the two to the same answers.
 */

/** The types version 1 knows. `image` is reserved for a later round and refused by name. */
export type FactsFieldType = "text" | "number" | "price" | "opening_hours" | "phone" | "email" | "url" | "list";

/** The types a list's item fields may have: no list inside a list, and no opening hours. */
export type FactsItemFieldType = "text" | "number" | "price" | "phone" | "email" | "url";

/**
 * How a price's `from` or `incl_vat` is governed: editable, or fixed by the schema to one value.
 */
export type FactsPriceOption = { editable: true } | { editable: false; value: boolean };

interface FactsFieldBase {
  /** `^[a-z][a-z0-9_]{0,47}$`, unique among its siblings. Stable: a value is stored under it. */
  key: string;
  /** Swedish, 1 to 80 characters, plain text. */
  label: string;
  required: boolean;
  /** Swedish, 1 to 200 characters, plain text. */
  help?: string;
  /** Text: 1 to 500. */
  max_length?: number;
  /** Text: a closed list of the values allowed. */
  allowed_values?: string[];
  /** Number: the bounds. Price: `max` only. */
  min?: number;
  max?: number;
  /** Number: whether only whole numbers are allowed. */
  integer?: boolean;
  /** Price. */
  from?: FactsPriceOption;
  incl_vat?: FactsPriceOption;
}

/** A field inside a list. It has no `editable`: the list's own decides. */
export interface FactsItemField extends FactsFieldBase {
  type: FactsItemFieldType;
}

export interface FactsField extends FactsFieldBase {
  type: FactsFieldType;
  /** Whether the client may change it (round 19). Always false for phone, email and url. */
  editable: boolean;
  /** List. */
  item_fields?: FactsItemField[];
  min_items?: number;
  max_items?: number;
}

export interface FactsSchema {
  version: 1;
  fields: FactsField[];
}

export interface FactsPrice {
  /** At least 0, at most the field's `max`, at most two decimals. */
  amount: number;
  from: boolean;
  incl_vat: boolean;
}

export const OPENING_DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type OpeningDay = (typeof OPENING_DAYS)[number];

export interface FactsOpeningDay {
  day: OpeningDay;
  closed: boolean;
  /** `HH:MM`, or null when closed. */
  from: string | null;
  to: string | null;
}

/** A value of any type; null means no value. */
export type FactsValue =
  | string
  | number
  | FactsPrice
  | FactsOpeningDay[]
  | Array<Record<string, FactsValue>>
  | null;

/** A facts document: every top-level key of the schema to its value or null. */
export type FactsValues = Record<string, FactsValue>;

/**
 * One reason a schema or a set of values is refused.
 *
 * `key` is a path: a field's key, `fields[3]` for a field with no usable key, `$` for the whole
 * document, `list_key[2].item_key` inside a list, `hours[0]` for a day of opening hours. `code` is one
 * of the codes in `docs/facts-schema.md`.
 */
export interface FactsProblem {
  key: string;
  code: string;
}
